// @flow
import gql from "graphql-tag"
import { path, pathOr } from "ramda"
import type { Query } from "common/graphql/types"
import type { ID } from "data/enums/types"
import type { DateString } from "data/units/date/types"
import type { ProductGroup, ProductGroupName } from "data/app/types"
import { paginationFragment } from "data/app/graphql/fragments"
import type { ProductOffering } from "data/product/types"
import type { Manufacturer } from "data/manufacturer/types"
import { productOfferingFragment } from "data/product/graphql/fragments"
import type { AffiliateFull, AffiliateMinimal } from "data/affiliate/types"
import { affiliateFragmentFull, affiliateFragmentMinimal } from "data/affiliate/fragments"
import type { EnumFilter, RangeFilter } from "data/search/graphql/queries/search"

export type AffiliateDetailQueryVariables = {
  id: ID,

  start: ?DateString,
  end: ?DateString,

  manufacturerIds: ?(ID[]),
  locationIds: ?ID[],
  discountId: ?number,
  productGroups: ?(ProductGroupName[]),

  rangeFilters: ?(RangeFilter[]),
  enumFilters: ?(EnumFilter[]),
}

/**
 * Query to support the Affiliate Detail page. Includes affiliate data and
 * filtered list of product offerings.
 */
export const affiliateDetailQuery: Query<
  {
    affiliate: AffiliateFull,
    productOfferings: ProductOffering[],
    manufacturers: Manufacturer[],
  },
  AffiliateDetailQueryVariables
> = {
  gql: gql`
    query affiliateDetailQuery(
      $id: Int!

      $start: String
      $end: String

      $productIds: [Int!]
      $locationIds: [Int!]
      $manufacturerIds: [Int!]
      $productGroups: [String]
      $discountId: Int

      $rangeFilters: [RangeFilter!]
      $enumFilters: [EnumFilter!]

      $limit: Int
      $offset: Int
    ) {
      affiliate {
        get(id: $id) {
          ...affiliateFragmentFull
        }
      }
      product {
        listProductOfferings(
          filter: {
            start: $start
            end: $end

            productIds: $productIds
            locationIds: $locationIds
            manufacturerIds: $manufacturerIds
            productGroups: $productGroups
            discountId: $discountId
            affiliateId: $id

            rangeFilters: $rangeFilters
            enumFilters: $enumFilters

            limit: $limit
            offset: $offset
          }
        ) {
          pageInfo {
            ...paginationFragment
          }
          data {
            ...productOfferingFragment
          }
        }
      }
      manufacturer {
        list(
          filter: {
            limit: 999
            affiliateIds: [$id]
          }
        ) {
          data {
            id
            name
          }
        }
      }
    }
    ${paginationFragment}
    ${affiliateFragmentFull}
    ${productOfferingFragment}
  `,
  transform: data => ({
    // $Ramda
    affiliate: path(["affiliate", "get"], data),
    // $Ramda
    productOfferings: pathOr([], ["product", "listProductOfferings", "data"], data),
    // $Ramda
    manufacturers: pathOr([], ["manufacturer", "list", "data"], data)
  }),
  pagination: ["product", "listProductOfferings"],
}

export const nearbyStoresQuery: Query<
  AffiliateMinimal[],
  { productGroups: ?ProductGroupName[], locationIds: ?ID[], discountId: ?number, limit: ?number },
> = {
  gql: gql`
    query nearbyStoresQuery($productGroups: [String], $locationIds: [Int!], $discountId: Int, $limit: Int) {
      affiliate {
        listNearby(
          filter: {
            limit: $limit
            productGroups: $productGroups
            locationIds: $locationIds
            discountId: $discountId
          }
        ) {
          data {
            ...affiliateFragmentMinimal
          }
        }
      }
    }
    ${affiliateFragmentMinimal}
  `,
  selector: ["affiliate", "listNearby", "data"],
}

export const affiliateCurrentDate: Query<
  {
    affiliateCurrentDate: DateString,
  },
  {|
    affiliateId: ID,
  |},
> = {
  gql: gql`
    query affiliateCurrentDate(
      $affiliateId: Int!,
    ) {
      affiliate {
        getCurrentDate(id: $affiliateId)
      }
    }
  `,
  transform: data => ({
    // $Ramda
    affiliateCurrentDate: path(["affiliate", "getCurrentDate"], data),
  }),
  options: () => ({
    fetchPolicy: "network-only",
  }),
}

/** Fetches the product groups offered by an affiliate */
export const affiliateSupportedProductGroupsQuery: Query<
  {
    supportedProductGroups: ProductGroup[],
  }
> = {
  gql: gql`
    query affiliateSupportedProductGroupsQuery($id: Int!) {
      affiliate {
        get(id: $id) {
          productGroups {
            id
            name
          }
        }
      }
    }
  `,
  selector: ["affiliate", "get", "productGroups"],
}



// WEBPACK FOOTER //
// ./src/data/affiliate/graphql.js